import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import {
  Group,
  Admin,
  roles_roleName,
  UserSuggestion,
} from 'src/app/shared/models/interfaces';
import { GroupAdminsService } from './group-admins.service';
import { GroupsService } from '../groups/groups.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ConfirmDialogComponent } from 'src/app/shared/ui-components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-group-admins',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    UIComponentsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
  ],
  templateUrl: './group-admins.component.html',
  styleUrls: ['./group-admins.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', visibility: 'hidden' })
      ),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class GroupAdminsComponent implements OnInit, OnDestroy {
  dataSource: Admin[] = [];
  columnsToDisplay = [
    'firstName',
    'lastName',
    'email',
    'role',
    'numberOfGroups',
  ];
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand'];
  expandedElement: Admin | null = null;
  private subscriptions = new Subscription();

  public RolesEnum = roles_roleName;

  userSearchForm: FormGroup = this.initUserSerachForm();
  userSuggestions: UserSuggestion[] = [];

  private destroy$ = new Subject<void>();

  filteredUsers: UserSuggestion[] = [];

  constructor(
    private groupAdminsService: GroupAdminsService,
    public groupsService: GroupsService,
    private snackBar: SnackbarService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.fetchGroupAdmins();
    this.userSerachSubscription();
  }

  private initUserSerachForm(): FormGroup {
    return new FormGroup({
      userSearchCtrl: new FormControl<UserSuggestion | null>(
        null,
        Validators.required
      ),
    });
  }

  userSerachSubscription() {
    this.userSearchForm
      .get('userSearchCtrl')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) =>
          this.groupAdminsService.searchUsersForAdminPromotion(value ?? '')
        ),

        takeUntil(this.destroy$)
      )
      .subscribe((results) => (this.userSuggestions = results));
  }

  displayUser = (user: UserSuggestion | string): string => {
    return user ? `${typeof user === 'string' ? user : user.email}` : '';
  };

  onAddAdmin() {
    if (this.userSearchForm.invalid) {
      this.snackBar.showWarningMessage(
        'Please select a user to promote to Group Admin.'
      );
      return;
    }
    const selectedUser = this.userSearchForm.get('userSearchCtrl')?.value;
    if (!selectedUser) {
      this.snackBar.showWarningMessage(
        'No user selected. Please select a user to promote.'
      );
      return;
    }
    this.subscriptions.add(
      this.groupAdminsService
        .promoteToGroupAdmin(selectedUser.id)
        .pipe(
          tap((newAdmin) => {
            this.snackBar.showSuccessMessage(
              `${newAdmin.email} has been promoted to Group Admin.`
            );
            // Refresh the admin list after promotion
            this.fetchGroupAdmins();
            this.userSearchForm.reset();
          }),
          catchError((err) => {
            console.error('Error promoting user to admin:', err);

            return of(null);
          })
        )
        .subscribe()
    );
  }

  fetchGroupAdmins() {
    this.subscriptions.add(
      this.groupAdminsService.getAdminsWithGroupDetails().subscribe({
        next: (admins) => {
          this.dataSource = admins;
        },
        error: (err) => {
          console.error('Error fetching group admins:', err);
          this.snackBar.showErrorMessage(
            'Failed to fetch group admins. Please try again later.'
          );
        },
      })
    );
  }

  onRemoveAdminRights(admin: Admin) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Remove Admin Rights for ${admin.email}`,
        message: `Are you sure you want to remove Group admin rights from ${admin.email}? This will make them a regular user.`,
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.subscriptions.add(
          this.groupAdminsService.demoteToUser(admin.id).subscribe({
            next: (updatedAdmin) => {
              this.snackBar.showSuccessMessage(
                `${admin.email} has been demoted to a regular user.`
              );
              this.fetchGroupAdmins();
            },
            error: (err) => {
              console.error(`Error demoting ${admin.email}:`, err);
              this.snackBar.showErrorMessage(
                `Failed to remove admin rights from ${admin.email}.`
              );
            },
          })
        );
      }
    });
  }

  onAddSuperAdminRights(admin: Admin) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Promote to Super Admin`,
        message: `Are you sure you want to promote ${admin.email} to Super Admin? This will give them the rights to add and remove Group Admins and grant super admin rights.`,
        confirmButtonText: 'Promote',
        cancelButtonText: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.subscriptions.add(
          this.groupAdminsService.promoteToSuperAdmin(admin.id).subscribe({
            next: (updatedAdmin) => {
              this.snackBar.showSuccessMessage(
                `${admin.email} has been promoted to Super Admin.`
              );
              this.fetchGroupAdmins();
            },
            error: (err) => {
              console.error(`Error promoting ${admin.email}:`, err);
              this.snackBar.showErrorMessage(
                `Failed to promote ${admin.email} to Super Admin.`
              );
            },
          })
        );
      }
    });
  }

  isExpanded(element: Admin): boolean {
    return this.expandedElement === element;
  }

  toggle(element: Admin): void {
    this.expandedElement = this.isExpanded(element) ? null : element;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
